import {GitHubAvatar} from '@github-ui/github-avatar'
import {sendEvent} from '@github-ui/hydro-analytics'
import {InlineAutocomplete} from '@github-ui/inline-autocomplete'
import type {ShowSuggestionsEvent, Suggestion} from '@github-ui/inline-autocomplete/types'
import {
  CommentDiscussionIcon,
  DiscussionClosedIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
  IssueClosedIcon,
  IssueDraftIcon,
  IssueOpenedIcon,
  RepoIcon,
} from '@primer/octicons-react'
import {ActionList, Truncate} from '@primer/react'
import {hasMatch} from 'fzy.js'
import type {DetailedHTMLProps, InputHTMLAttributes, ReactElement} from 'react'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {AGENT_PREFIX, useAvailableAgents} from '../utils/agents-helpers'
import {
  AUTOCOMPLETE_AGENT_PREFIX,
  AUTOCOMPLETE_PREFIX,
  getDiscussions,
  getIssues,
  getPullRequests,
  getRepos,
} from '../utils/autocomplete-helpers'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
import {useChatState} from '../utils/CopilotChatContext'

const supportedTypes = ['issues', 'pulls', 'discussions']

const getSuggestionValue = (suggestion?: Suggestion) =>
  suggestion && (typeof suggestion === 'string' ? suggestion : suggestion?.value)

export function Autocomplete({
  children,
}: {
  children: ReactElement<DetailedHTMLProps<InputHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>>
}) {
  const {mode} = useChatState()

  const [mounted, setMounted] = useState(() => typeof document !== 'undefined')
  useEffect(() => setMounted(true), [])

  const [showEvent, setShowEvent] = useState<ShowSuggestionsEvent | null>(null)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])

  const triggerChars = useMemo(() => {
    if (copilotFeatureFlags.chatAutocomplete) {
      return [
        {triggerChar: AUTOCOMPLETE_PREFIX, insertSpaceOnCommit: false},
        {triggerChar: AUTOCOMPLETE_AGENT_PREFIX, insertSpaceOnCommit: false},
      ]
    } else {
      return [{triggerChar: AGENT_PREFIX, insertSpaceOnCommit: true}]
    }
  }, [])

  const agents = useAvailableAgents(showEvent?.trigger.triggerChar === AGENT_PREFIX)

  const getRepoItems = useCallback(
    async (query: string, appendedValue: string = '') => {
      const repos = await getRepos(query)
      return repos.map<Suggestion>(repo => ({
        value: `${repo.nameWithOwner}${appendedValue}`,
        key: repo.nameWithOwner,
        render: $props => (
          <ActionList.Item
            key={repo.nameWithOwner}
            {...$props}
            onSelect={() =>
              sendEvent('dotcom_chat.activate', {
                target: 'REPO_MENU_ITEM_SELECTED',
                mode,
                command: repo.nameWithOwner,
              })
            }
          >
            <ActionList.LeadingVisual>
              <RepoIcon />
            </ActionList.LeadingVisual>
            <span className="text-normal">{repo.name}</span>
            <ActionList.Description variant="inline" truncate>
              {repo.owner}
            </ActionList.Description>
          </ActionList.Item>
        ),
      }))
    },
    [mode],
  )

  useEffect(() => {
    async function fetchNewSuggestions() {
      if (!showEvent) return

      const queryText = showEvent.query.toLowerCase()
      const matchesQuery = (item: string) => {
        const lc = item.toLowerCase()
        return lc.startsWith(queryText) && lc !== queryText
      }

      // Check for `@` first for agent suggestions
      // If ChatAutocomplete is enabled, we instead check for `@agent:`
      if (
        (!copilotFeatureFlags.chatAutocomplete && showEvent.trigger.triggerChar === AGENT_PREFIX) ||
        (copilotFeatureFlags.chatAutocomplete && showEvent.trigger.triggerChar === AUTOCOMPLETE_AGENT_PREFIX)
      ) {
        if (agents.disabled) setSuggestions([])
        if (agents.loading) setSuggestions(['loading'])
        if (queryText === '') {
          sendEvent('dotcom_chat.activate', {target: 'AGENT_COMMAND_MENU_TRIGGERED', mode})
        }

        const matchingAgents = agents.availableAgents?.filter(agent => matchesQuery(agent.slug)).slice(0, 10) ?? []

        setSuggestions(
          matchingAgents.map<Suggestion>(agent => ({
            value: agent.slug,
            key: agent.slug,
            render: $props => (
              <ActionList.Item
                key={agent.slug}
                {...$props}
                onSelect={() =>
                  sendEvent('dotcom_chat.activate', {
                    target: 'AGENT_COMMAND_MENU_ITEM_SELECTED',
                    mode,
                    command: agent.slug,
                  })
                }
              >
                <ActionList.LeadingVisual>
                  <GitHubAvatar src={agent.avatarUrl} />
                </ActionList.LeadingVisual>
                {agent.name}
                <ActionList.Description variant="inline" truncate>
                  {AGENT_PREFIX}
                  {agent.slug}
                </ActionList.Description>
                {agents.showLimit && (
                  <ActionList.Description variant="block">
                    You&apos;re limited to one agent per thread.
                  </ActionList.Description>
                )}
              </ActionList.Item>
            ),
          })),
        )
        return
      }

      if (copilotFeatureFlags.chatAutocomplete) {
        // Exmaple input: @github/github/issues/123
        // Split the string into 3 parts: @github/github, issues, 123
        const repoRegex = /^([\w-.]*\/?[\w-.]*)$/
        const typeRegex = /^([\w-.]+\/[\w-.]+)\/(\w*)$/
        const itemRegex = /^([\w-.]+\/[\w-.]+)\/(\w+)\/([\d\w]*)$/

        const matchResults = queryText.match(itemRegex) ?? []
        let repo: string | undefined
        let type: string | undefined
        const itemRef = matchResults[3] ?? undefined
        ;[, repo, type] = matchResults
        if (repo !== undefined && type !== undefined && itemRef !== undefined) {
          // Continue filtering by item number or name
          if (supportedTypes.includes(type)) {
            let items: Suggestion[] = []

            switch (type) {
              case 'issues': {
                const issues = await getIssues(repo, itemRef)
                items = issues.map<Suggestion>(issue => ({
                  render: p => (
                    <ActionList.Item {...p}>
                      <ActionList.LeadingVisual>
                        {issue.state === 'draft' && <IssueDraftIcon className="fgColor-neutral" />}
                        {issue.state === 'open' && <IssueOpenedIcon className="fgColor-open" />}
                        {issue.state === 'closed' && <IssueClosedIcon className="fgColor-done" />}
                      </ActionList.LeadingVisual>
                      <Truncate title={issue.title} maxWidth={350}>
                        {issue.title}
                      </Truncate>
                      <ActionList.TrailingVisual style={{marginLeft: '0.5rem'}}>
                        #{issue.number}
                      </ActionList.TrailingVisual>
                    </ActionList.Item>
                  ),
                  value: `${repo}/issues/${issue.number} `,
                }))

                setSuggestions(items)
                break
              }
              case 'pulls': {
                const pulls = await getPullRequests(repo, itemRef)
                items = pulls.map<Suggestion>(pull => ({
                  render: p => (
                    <ActionList.Item {...p}>
                      <ActionList.LeadingVisual>
                        {pull.state === 'draft' && <GitPullRequestDraftIcon className="fgColor-warning" />}
                        {pull.state === 'open' && <GitPullRequestIcon className="fgColor-open" />}
                        {pull.state === 'closed' && <GitPullRequestClosedIcon className="fgColor-done" />}
                      </ActionList.LeadingVisual>
                      <Truncate title={pull.title} maxWidth={350}>
                        {pull.title}
                      </Truncate>
                      <ActionList.TrailingVisual style={{marginLeft: '0.5rem'}}>
                        #{pull.number}
                      </ActionList.TrailingVisual>
                    </ActionList.Item>
                  ),
                  value: `${repo}/pulls/${pull.number} `,
                }))

                setSuggestions(items)
                break
              }
              case 'discussions': {
                const discussions = await getDiscussions(repo, itemRef)
                items = discussions.map<Suggestion>(discussion => ({
                  render: p => (
                    <ActionList.Item {...p}>
                      <ActionList.LeadingVisual>
                        {discussion.state === 'open' && <CommentDiscussionIcon className="fgColor-open" />}
                        {discussion.state === 'closed' && <DiscussionClosedIcon className="fgColor-muted" />}
                      </ActionList.LeadingVisual>
                      <Truncate title={discussion.title} maxWidth={350}>
                        {discussion.title}
                      </Truncate>
                      <ActionList.TrailingVisual style={{marginLeft: '0.5rem'}}>
                        #{discussion.number}
                      </ActionList.TrailingVisual>
                    </ActionList.Item>
                  ),
                  value: `${repo}/discussions/${discussion.number} `,
                }))
                break
              }
            }

            if (items.length > 1 || getSuggestionValue(items[0]) !== `${repo}/${type}/${itemRef}`) {
              setSuggestions(items)
            } else {
              setSuggestions([])
            }
          }
        }

        ;[, repo, type] = queryText.match(typeRegex) ?? []
        if (repo !== undefined && type !== undefined) {
          const typeItems = (
            [
              {
                value: `${repo} `,
                render: p => <ActionList.Item {...p}>Repository</ActionList.Item>,
              },
              {
                value: `${repo}/discussions/`,
                render: p => <ActionList.Item {...p}>Discussion</ActionList.Item>,
              },
              {
                value: `${repo}/issues/`,
                render: p => <ActionList.Item {...p}>Issue</ActionList.Item>,
              },
              {
                value: `${repo}/pulls/`,
                render: p => <ActionList.Item {...p}>Pull Request</ActionList.Item>,
              },
            ] as Suggestion[]
          ).filter(acItem => hasMatch(type, getSuggestionValue(acItem) ?? ''))

          if (typeItems.length > 1 || getSuggestionValue(typeItems[0]) !== `${repo}/${type}/`) {
            setSuggestions(typeItems)
          } else {
            setSuggestions([])
          }
        }

        ;[, repo] = queryText.match(repoRegex) ?? []
        if (repo !== undefined) {
          const repoItems = await getRepoItems(queryText, '/')
          if (!queryText || hasMatch('agent', queryText)) {
            repoItems.unshift({
              value: `agent:`,
              render: p => <ActionList.Item {...p}>Agent</ActionList.Item>,
            })
          }
          if (repoItems.length > 1 || getSuggestionValue(repoItems[0]) !== `${repo}/`) {
            setSuggestions(repoItems)
          } else {
            setSuggestions([])
          }
        }
      }
    }

    if (!showEvent) {
      setSuggestions([])
    } else {
      // Set Loading State
      void fetchNewSuggestions()
    }
  }, [agents.availableAgents, agents.disabled, agents.loading, agents.showLimit, getRepoItems, mode, showEvent])

  const onShowSuggestions = useCallback((e: ShowSuggestionsEvent) => {
    setShowEvent(e)
  }, [])

  const onHideSuggestions = useCallback(() => {
    setShowEvent(null)
  }, [])

  if (!mounted) {
    return <div>{children}</div>
  }

  return (
    <InlineAutocomplete
      fullWidth
      suggestions={suggestions}
      triggers={triggerChars}
      tabInsertsSuggestions={false}
      onHideSuggestions={onHideSuggestions}
      onShowSuggestions={onShowSuggestions}
    >
      {children}
    </InlineAutocomplete>
  )
}

try{ Autocomplete.displayName ||= 'Autocomplete' } catch {}